import type { LotResponse } from '@/api/generated/model';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  FEFO — SUGGESTED IS NOT CHOSEN, AND THE DIFFERENCE IS THE WHOLE POINT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * First-Expired-First-Out: of the lots that can actually be drawn from today,
 * the one whose expiry comes soonest and which still holds the whole quantity.
 * That is a *suggestion*. What the ledger records is the lot the doctor
 * actually opened, whether or not it was the suggested one.
 *
 * ── WHY THE PANEL DERIVES IT AT ALL ─────────────────────────────────────────
 *
 * The server suggests too — `GET /sessions/consumption-suggestions` returns a
 * `FefoSuggestionResponse` per default consumable, and `POST /sessions` runs
 * the same allocator again at write time. Neither is enough on its own for a
 * form: the moment the operator edits the quantity, adds a material the
 * procedure has no default for, or marks a line as wastage, the server's
 * suggestion is about a different question than the one on screen. A badge
 * reading «پیشنهاد FEFO» next to a lot that is no longer the FEFO answer for
 * the quantity now in the field is worse than no badge, because the override
 * flag and the mandatory reason both hang off that comparison.
 *
 * So: the server's suggestion seeds the line, this function keeps it honest
 * while the line is edited, and the server re-runs its own allocator on submit.
 * Three agreeing answers, and a visible disagreement if they ever stop agreeing.
 *
 * ── WHAT "ACTUALLY DRAWABLE" MEANS ──────────────────────────────────────────
 *
 * `LotResponse.isConsumable` is the server's own answer to "may this lot be
 * drawn from right now" — it folds in `status` (ACTIVE, not EXPIRED, not
 * QUARANTINED, not DEPLETED) and the expiry date. It is used rather than
 * re-deriving the rule here, because a panel that decides for itself which lots
 * are usable will eventually disagree with the allocator that performs the
 * write, and the operator will be told the batch they picked does not exist.
 *
 * ── THE TIE-BREAKS, AND WHY THEY ARE NOT ARBITRARY ──────────────────────────
 *
 * A lot with NO expiry date never wins on expiry: consumables with no shelf
 * life would otherwise permanently outrank a vial expiring on Thursday, which
 * inverts the whole policy. They are the last resort, ordered by the smallest
 * sufficient remainder so opened stock is finished before new stock is broken
 * into. Equal expiry falls back to the same rule, then to the id, so the
 * suggestion is stable across re-renders instead of flickering between two
 * equally-good lots.
 */

/** A lot that may be drawn from today, for the purposes of a suggestion. */
export function isDrawableLot(lot: LotResponse): boolean {
  return lot.isConsumable && lot.quantityRemainingMilli > 0;
}

/** Epoch ms of a lot's expiry, or `null` for stock that does not expire. */
function expiryTime(lot: LotResponse): number | null {
  if (!lot.expiryDate) return null;
  const time = new Date(lot.expiryDate).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * Orders lots the way FEFO wants them offered: soonest expiry first, undated
 * stock last, smallest sufficient remainder before largest, id as the final
 * tie-break so the order never changes between renders.
 *
 * Exported because the override dropdown must be in this order too — the point
 * of making the override "easy and explicit" is that the operator can see the
 * suggestion sitting at the top of the list and step past it deliberately.
 */
export function orderLotsByFefo(lots: readonly LotResponse[]): LotResponse[] {
  return [...lots].sort((a, b) => {
    const aExpiry = expiryTime(a);
    const bExpiry = expiryTime(b);
    if (aExpiry !== bExpiry) {
      if (aExpiry === null) return 1;
      if (bExpiry === null) return -1;
      return aExpiry - bExpiry;
    }
    if (a.quantityRemainingMilli !== b.quantityRemainingMilli) {
      return a.quantityRemainingMilli - b.quantityRemainingMilli;
    }
    return a.id.localeCompare(b.id);
  });
}

export interface FefoSuggestion {
  /** The suggested lot, or `null` when no single lot holds the quantity. */
  lot: LotResponse | null;
  /** Total drawable stock of this material across every usable lot. */
  availableMilli: number;
  /** `true` when stock exists but no single lot covers the quantity. */
  insufficient: boolean;
}

/**
 * The FEFO answer for `quantityMilli` of one material.
 *
 * A single lot must cover the whole quantity. Splitting a draw across lots is
 * the server's decision (`allowSplit` on the allocator), not the form's: a
 * split writes two `SessionConsumption` rows at two different unit costs, and
 * the operator has to be shown that before it happens rather than after.
 */
export function suggestFefoLot(
  lots: readonly LotResponse[],
  quantityMilli: number,
): FefoSuggestion {
  const drawable = lots.filter(isDrawableLot);
  const availableMilli = drawable.reduce((sum, lot) => sum + lot.quantityRemainingMilli, 0);

  if (!Number.isFinite(quantityMilli) || quantityMilli <= 0) {
    return { lot: null, availableMilli, insufficient: false };
  }

  const sufficient = orderLotsByFefo(drawable).filter(
    (lot) => lot.quantityRemainingMilli >= quantityMilli,
  );

  return {
    lot: sufficient[0] ?? null,
    availableMilli,
    insufficient: sufficient.length === 0,
  };
}

/**
 * Did the operator step past the suggestion?
 *
 * `false` when there was no suggestion to step past — a material with no usable
 * lot at all is a stock problem, not an override, and demanding a written
 * justification for it would train people to type «-» into the reason box.
 */
export function isFefoOverride(
  suggestedLotId: string | null | undefined,
  chosenLotId: string | null | undefined,
): boolean {
  if (!suggestedLotId || !chosenLotId) return false;
  return suggestedLotId !== chosenLotId;
}
